import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useContextSite } from "../../../context/Context";
import { Cliente } from "../../../services/Cliente";
import { IClienteDTO, IClienteGetProps } from "../../../types/cliente";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { useMediaQuery } from "react-responsive";
import { IPagination } from "../../../types/pagination";
import { resetValues } from "../../../utils/resetObject";
import {
  maskCnpj,
  maskCpf,
  maskPhone,
  removerCaracteresEspeciais,
} from "../../../utils/masks";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSessionStorage } from "../../../hooks/useSessionStorage";

const schema = z.object({
  cpfCnpj: z
    .string()
    .optional()
    .or(z.literal(""))
    .transform((val) => removerCaracteresEspeciais(val)),
  email: z.string().optional().or(z.literal("")),
  nome: z.string().optional().or(z.literal("")),
  telefone: z.string().optional().or(z.literal("")),
});

export const useClients = () => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const { setIsLoad } = useContextSite();
  const isMobile = useMediaQuery({ maxWidth: "640px" });
  const [clientes, setClientes] = useState<IClienteDTO[]>([] as IClienteDTO[]);
  const [pagination, setPagination] = useState<IPagination>({} as IPagination);
  const [numberPage, setNumberPage] = useState(0);
  const [filter, setFilter] = useState<IClienteGetProps>(
    {} as IClienteGetProps
  );
  const [usuario] = useSessionStorage("@usuarioMapa");
  const isAdmin = usuario?.roles?.includes("ROLE_ADMIN");

  const canAccessClients =
    isAdmin || usuario?.recursos?.includes("CONFIGURACAO_CLIENTE_VER");
  const canEditClients =
    isAdmin || usuario?.recursos?.includes("CONFIGURACAO_CLIENTE_EDITAR");
  const { register, reset, handleSubmit, watch, setValue } =
    useForm<IClienteGetProps>({
      defaultValues: {
        cpfCnpj: "",
        email: "",
        nome: "",
        telefone: "",
      },
      resolver: zodResolver(schema),
    });

  function getClientes(props?: IClienteGetProps) {
    setIsLoad(true);

    Cliente.get({ size: 5, ...props })
      .then(({ data }) => {
        setClientes(data.content);
        setPagination({
          actualPage: data.number,
          totalPage: data.totalPages,
          totalRegister: data.totalElements,
        });
      })
      .catch(
        ({
          response: {
            data: { mensagem },
          },
        }) => {
          toast.error(mensagem);
        }
      )
      .finally(() => {
        setIsLoad(false);
      });
  }

  function submitFilter(data: IClienteGetProps) {
    setNumberPage(0);
    setFilter(data);
    getClientes({ page: 0, ...data });
  }

  function handleReset() {
    reset();
    const empty = resetValues(filter);
    setFilter(empty);
    setNumberPage(0);

    getClientes({ page: 0 });
  }

  useEffect(() => {
    getClientes({ page: numberPage, ...filter });
  }, [numberPage]);

  useEffect(() => {
    if (watch("cpfCnpj")?.length > 14) {
      setValue("cpfCnpj", maskCnpj(watch("cpfCnpj")));
      return;
    }

    setValue("cpfCnpj", maskCpf(watch("cpfCnpj")));
  }, [watch("cpfCnpj")]);

  useEffect(() => {
    setValue("telefone", maskPhone(watch("telefone")));
  }, [watch("telefone")]);

  return {
    isOpen,
    setIsOpen,
    navigate,
    clientes,
    isMobile,
    pagination,
    setNumberPage,
    handleSubmit,
    register,
    handleReset,
    submitFilter,
    canAccessClients,
    canEditClients,
  };
};
